// A Page is a Result of kind `html` (docs/plans/poneva-everywhere-2026-10-16/PAGES.md).
// Its payload arrives from the server, and in the Poneva view from another company's
// host, so it is read here once, bounded and checked, before anything draws it.

export type PageResource = {
  /** The address the page's own html uses (an https CDN address). */
  url: string;
  /** 64 hex characters: the bytes Poneva serves at /everywhere/vendor/<sha256>. */
  sha256: string;
  type: 'script' | 'style' | 'font';
  bytes: number;
};
export type PagePayload = {
  html: string;
  /** Plain text of the page (search and static previews). */
  text: string;
  resources: PageResource[];
  /** How many files Poneva could not carry over. Only the number is drawn; the agent has the wording. */
  noteCount: number;
};

export const MAX_PAGE_HTML = 512 * 1024;
export const MAX_PAGE_TEXT = 20_000;
export const MAX_PAGE_RESOURCES = 60;
const MAX_URL = 2048;
const MAX_NOT_COPIED = 100_000;

const isBag = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function resource(value: unknown): PageResource[] {
  if (!isBag(value)) return [];
  const { url, sha256, type, bytes } = value;
  if (typeof url !== 'string' || url.length > MAX_URL || !/^https:\/\//u.test(url)) return [];
  if (typeof sha256 !== 'string' || !/^[0-9a-f]{64}$/u.test(sha256)) return [];
  if (type !== 'script' && type !== 'style' && type !== 'font') return [];
  const size = typeof bytes === 'number' && Number.isFinite(bytes) && bytes >= 0 ? bytes : 0;
  return [{ url, sha256, type, bytes: size }];
}

// The server's total (the notes keep only the first few); a page saved before the total existed
// has its notes counted.
function notCopied(value: Record<string, unknown>): number {
  const total = value.notCopied;
  if (typeof total === 'number' && Number.isSafeInteger(total) && total >= 0)
    return Math.min(total, MAX_NOT_COPIED);
  return Array.isArray(value.notes) ? Math.min(value.notes.length, MAX_NOT_COPIED) : 0;
}

/** The page a payload holds, or null when it holds none that can run. */
export function readPagePayload(value: unknown): PagePayload | null {
  if (!isBag(value) || typeof value.html !== 'string' || !value.html.trim()) return null;
  if (value.html.length > MAX_PAGE_HTML) return null;
  const resources = (Array.isArray(value.resources) ? value.resources : [])
    .slice(0, MAX_PAGE_RESOURCES)
    .flatMap(resource);
  return {
    html: value.html,
    text: typeof value.text === 'string' ? value.text.slice(0, MAX_PAGE_TEXT) : '',
    resources,
    noteCount: notCopied(value),
  };
}
